import { useState, useEffect } from 'react';
import { Plus, Search, CheckSquare, Circle, CheckCircle2, Clock } from 'lucide-react';
import { Button, Input, Card, Pagination, SortableHeader, AdvancedSearch, type SortDirection } from '../components/ui';
import { API_BASE_URL, getAuthToken } from '../lib/api';

interface Todo {
  id: string;
  todoId: string;
  todoTitle: string;
  todoType: string;
  todoStatus: string;
  todoDueDate: string;
  daysLeft: number;
  ownerName: string;
  linkType: string;
}

const TODO_STATUS_OPTIONS = [
  { value: 'pending', label: '未着手' },
  { value: 'in_progress', label: '進行中' },
  { value: 'completed', label: '完了' },
];

const TODO_TYPE_OPTIONS = [
  { value: 'フォローアップ', label: 'フォローアップ' },
  { value: '提案書作成', label: '提案書作成' },
  { value: '見積作成', label: '見積作成' },
  { value: '電話', label: '電話' },
  { value: 'メール', label: 'メール' },
  { value: 'その他', label: 'その他' },
];

const SEARCH_FIELDS = [
  { key: 'todoTitle', label: 'タイトル', type: 'text' as const, placeholder: 'タイトル...' },
  { key: 'todoType', label: '種別', type: 'select' as const, options: TODO_TYPE_OPTIONS },
  { key: 'todoStatus', label: 'ステータス', type: 'select' as const, options: TODO_STATUS_OPTIONS },
  { key: 'ownerName', label: '担当者', type: 'text' as const, placeholder: '担当者名...' },
  { key: 'todoDueDate', label: '期限', type: 'daterange' as const },
];

export function Todos() {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // 検索条件
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // ページネーション
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // ソート
  const [sortKey, setSortKey] = useState<string | null>('todoDueDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    fetchTodos();
  }, [page, limit, sortKey, sortDirection, statusFilter]);

  const fetchTodos = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(sortKey && sortDirection && { sortBy: sortKey, sortOrder: sortDirection }),
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== '')
        ),
      });
      
      const headers: Record<string, string> = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/todos?${params}`, {
        credentials: 'include',
        headers,
      });
      const data = await response.json();
      
      setTodos(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error('Fetch todos error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchTodos();
  };

  const handleReset = () => {
    setSearch('');
    setFilters({});
    setStatusFilter('all');
    setPage(1);
    fetchTodos();
  };

  const handleSort = (key: string, direction: SortDirection) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction);
    setPage(1);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleStatusChange = async (todoId: string, newStatus: string) => {
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      await fetch(`${API_BASE_URL}/api/todos/${todoId}/status`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      fetchTodos();
    } catch (err) {
      console.error('Status update error:', err);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={20} className="text-green-500" />;
      case 'in_progress':
        return <Clock size={20} className="text-blue-500" />;
      default:
        return <Circle size={20} className="text-gray-400" />;
    }
  };

  const getDueBadge = (daysLeft: number | null, status: string) => {
    if (status === 'completed') return null;
    if (daysLeft === null) return null;
    
    if (daysLeft < 0) {
      return <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-700">期限超過</span>;
    }
    if (daysLeft === 0) {
      return <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-700">今日</span>;
    }
    if (daysLeft <= 3) {
      return <span className="px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-700">あと{daysLeft}日</span>;
    }
    return <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700">あと{daysLeft}日</span>;
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; className: string }> = {
      pending: { label: '未着手', className: 'bg-gray-100 text-gray-700' },
      in_progress: { label: '進行中', className: 'bg-blue-100 text-blue-700' },
      completed: { label: '完了', className: 'bg-green-100 text-green-700' },
    };
    return badges[status] || badges.pending;
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            ToDo管理
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            {total.toLocaleString()} 件のタスク
          </p>
        </div>
        <Button leftIcon={<Plus size={16} />}>
          新規作成
        </Button>
      </div>

      {/* フィルタータブ */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { key: 'all', label: 'すべて' },
          { key: 'pending', label: '未着手' },
          { key: 'in_progress', label: '進行中' },
          { key: 'completed', label: '完了' },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => {
              setStatusFilter(item.key);
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === item.key
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
            style={statusFilter !== item.key ? { 
              backgroundColor: 'var(--background-card)',
              border: '1px solid var(--border-color)',
            } : {}}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* 検索・フィルター */}
      <Card padding="sm">
        <AdvancedSearch
          fields={SEARCH_FIELDS}
          values={filters}
          onChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
          onSearch={handleSearch}
          onReset={handleReset}
        >
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="タイトルで検索..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search size={18} />}
            />
          </div>
          <Button type="button" onClick={handleSearch}>
            検索
          </Button>
        </AdvancedSearch>
      </Card>

      {/* テーブル */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
          </div>
        ) : todos.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <CheckSquare size={48} className="text-gray-300 mb-4" />
            <p style={{ color: 'var(--text-secondary)' }}>
              ToDoがありません
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="w-12 px-4 py-3" style={{ backgroundColor: 'var(--light-gray-primary)' }}></th>
                  <SortableHeader
                    label="タイトル"
                    sortKey="todoTitle"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="種別"
                    sortKey="todoType"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="ステータス"
                    sortKey="todoStatus"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="担当者"
                    sortKey="ownerName"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="期限"
                    sortKey="todoDueDate"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                {todos.map((todo) => {
                  const statusBadge = getStatusBadge(todo.todoStatus);
                  return (
                    <tr
                      key={todo.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => {
                            const nextStatus = todo.todoStatus === 'pending' 
                              ? 'in_progress' 
                              : todo.todoStatus === 'in_progress' 
                                ? 'completed' 
                                : 'pending';
                            handleStatusChange(todo.id, nextStatus);
                          }}
                          className="hover:scale-110 transition-transform"
                        >
                          {getStatusIcon(todo.todoStatus)}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p 
                          className={`font-medium ${todo.todoStatus === 'completed' ? 'line-through' : ''}`}
                          style={{ color: todo.todoStatus === 'completed' ? 'var(--text-secondary)' : 'var(--text-primary)' }}
                        >
                          {todo.todoTitle}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        {todo.todoType && (
                          <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 text-xs">
                            {todo.todoType}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${statusBadge.className}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>
                        {todo.ownerName || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getDueBadge(todo.daysLeft, todo.todoStatus)}
                          {todo.todoDueDate && (
                            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                              {new Date(todo.todoDueDate).toLocaleDateString('ja-JP', {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ページネーション */}
        {!isLoading && total > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
        )}
      </Card>
    </div>
  );
}
